import type {
  AutocompleteSource,
  GetSources,
} from "@algolia/autocomplete-shared";
import { IndexDocument } from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { getPageLink } from "@/navigation/selectors";
import { autocompleteClient } from "@/meilisearch";
import { getMeilisearchResults } from "@meilisearch/autocomplete-client";
import {
  AutocompleteTemplateHeader,
  AutocompleteTemplateItem,
  type AutocompleteTemplateItemProps,
} from "./SourceTemplates";
import {
  ArticleItem,
  BookItem,
  ContributorItem,
  LiteraryWorkItem,
  ProductBundleItem,
  SubscriptionItem,
  SeriesItem,
  DigitalServiceItem,
} from "../types";

type HighlightedDocument<Document> = {
  _highlightResult: {
    [K in keyof Document]: Document[K] extends Array<infer U> | undefined
      ? Array<{
          value: U;
          fullyHighlighted: boolean;
          matchLevel: string;
          matchedWords: string[];
        }>
      : {
          value: Document[K];
          fullyHighlighted: boolean;
          matchLevel: string;
          matchedWords: string[];
        };
  };
} & Document;

export const getSourcesWithFilters =
  ({ filters }: { filters?: string }): GetSources<IndexDocument> =>
  ({ query }) => {
    const source: AutocompleteSource<IndexDocument> = {
      sourceId: "items",
      getItemUrl({ item }) {
        const { slug, itemType } = item;
        if (itemType === "book") return `/boker/${slug}`;
        if (itemType === "product-bundle") return `/pakke/${slug}`;
        if (itemType === "subscription") return `/abonnement/${slug}`;
        if (itemType === "contributor") return `/forfatter/${slug}`;
        if (itemType === "literary-work") return `/verk/${slug}`;
        if (itemType === "series") return `/serie/${slug}`;
        if (itemType === "article")
          return getPageLink({
            pageType: "article",
            params: slug ?? undefined,
          });
        return "/search";
      },
      getItems() {
        return getMeilisearchResults<IndexDocument>({
          searchClient: autocompleteClient,
          queries: [
            {
              indexName: "items:immediacyScore:desc",
              query,
              params: {
                filters,
                attributesToHighlight: [
                  "name",
                  "itemDetails.contributorByLine",
                ],
                attributesToRetrieve: [
                  "name",
                  "slug",
                  "imageUrl",
                  "itemType",
                  "price",
                  "itemDetails.contributorByLine",
                  "itemDetails.binding",
                ],
                hitsPerPage: 3,
                distinct: query ? undefined : "name",
              },
            },
          ],
        }) as unknown as ReturnType<
          AutocompleteSource<IndexDocument>["getItems"]
        >;
      },
      templates: {
        header() {
          return <AutocompleteTemplateHeader>Treff</AutocompleteTemplateHeader>;
        },
        item({ item, components }) {
          const mapContributor = (
            i: HighlightedDocument<ContributorItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl } = i;
            return {
              imageSrc: imageUrl,
              altText: `Portrettbilde av ${name ?? "bidragsyter"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              itemType: "contributor",
              href: getPageLink({ pageType: "contributor", params: slug }),
            };
          };

          const mapBook = (
            i: HighlightedDocument<BookItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl, itemDetails } = i;
            return {
              imageSrc: imageUrl,
              altText: `Produktbilde: ${name ?? "bok"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              highlightDetails: components.Highlight({
                hit: item,
                attribute: ["itemDetails", "contributorByLine"],
              }),
              itemType: "book",
              format: itemDetails.binding ?? null,
              href: getPageLink({ pageType: "book", params: slug }),
            };
          };

          const mapProductBundle = (
            i: HighlightedDocument<ProductBundleItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl } = i;
            return {
              imageSrc: imageUrl,
              altText: `Produktbilde: ${name ?? "produktpakke"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              itemType: "book",
              href: getPageLink({ pageType: "productBundle", params: slug }),
            };
          };

          const mapSubscription = (
            i: HighlightedDocument<SubscriptionItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl } = i;
            return {
              imageSrc: imageUrl,
              altText: `Produktbilde: ${name ?? "abonnement"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              highlightDetails: "Abonnement",
              itemType: "book",
              href: getPageLink({ pageType: "subscription", params: slug }),
            };
          };

          const mapLiteraryWork = (
            i: HighlightedDocument<LiteraryWorkItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl } = i;
            return {
              imageSrc: imageUrl,
              altText: `${name ?? "verk"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              highlightDetails: "Verk",
              itemType: "literary-work",
              href: getPageLink({ pageType: "literaryWork", params: slug }),
            };
          };

          const mapSeries = (
            i: HighlightedDocument<SeriesItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl } = i;
            return {
              imageSrc: imageUrl,
              altText: `${name ?? "serie"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              itemType: "series",
              href: getPageLink({ pageType: "bookSeries", params: slug }),
            };
          };
          const mapDigitalService = (
            i: HighlightedDocument<DigitalServiceItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl } = i;
            return {
              imageSrc: imageUrl,
              altText: `${name ?? "Digital tjeneste"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              itemType: "series",
              href: `/digitale-tjenester/${slug}`,
            };
          };

          const mapArticle = (
            i: HighlightedDocument<ArticleItem>,
          ): AutocompleteTemplateItemProps => {
            const { name, slug, imageUrl, description } = i;
            return {
              imageSrc: imageUrl,
              altText: `${name ?? "artikkel"}`,
              highlightName: components.Highlight({
                hit: item,
                attribute: ["name"],
              }),
              highlightDetails: description ?? undefined,
              itemType: "article",
              href: getPageLink({ pageType: "article", params: slug }),
            };
          };

          switch (item.itemType) {
            case "book":
              return (
                <AutocompleteTemplateItem
                  {...mapBook(item as HighlightedDocument<BookItem>)}
                />
              );
            case "product-bundle":
              return (
                <AutocompleteTemplateItem
                  {...mapProductBundle(
                    item as HighlightedDocument<ProductBundleItem>,
                  )}
                />
              );
            case "subscription":
              return (
                <AutocompleteTemplateItem
                  {...mapSubscription(
                    item as HighlightedDocument<SubscriptionItem>,
                  )}
                />
              );
            case "contributor":
              return (
                <AutocompleteTemplateItem
                  {...mapContributor(
                    item as HighlightedDocument<ContributorItem>,
                  )}
                />
              );
            case "literary-work":
              return (
                <AutocompleteTemplateItem
                  {...mapLiteraryWork(
                    item as HighlightedDocument<LiteraryWorkItem>,
                  )}
                />
              );
            case "series":
              return (
                <AutocompleteTemplateItem
                  {...mapSeries(item as HighlightedDocument<SeriesItem>)}
                />
              );
            case "digital-service":
              return (
                <AutocompleteTemplateItem
                  {...mapDigitalService(
                    item as HighlightedDocument<DigitalServiceItem>,
                  )}
                />
              );
            case "article":
              return (
                <AutocompleteTemplateItem
                  {...mapArticle(item as HighlightedDocument<ArticleItem>)}
                />
              );
            default:
              return (
                <AutocompleteTemplateItem
                  imageSrc={(item as { imageUrl: string }).imageUrl}
                  highlightName={(item as { name: string }).name}
                  href={"#"}
                  altText=""
                />
              );
          }
        },
      },
    };
    return [source];
  };

export const getSourcesForEvaluationCopy =
  ({
    onSelect,
  }: {
    onSelect: AutocompleteSource<IndexDocument>["onSelect"];
  }): GetSources<IndexDocument> =>
  ({ query }) => {
    const bookSource: AutocompleteSource<IndexDocument> = {
      sourceId: "items",
      getItemUrl() {
        return "";
      },
      onSelect,
      getItems() {
        return getMeilisearchResults({
          searchClient: autocompleteClient,
          queries: [
            {
              indexName: "items:immediacyScore:desc",
              query,
              params: {
                filters: `(evaluationCopyType EXISTS AND evaluationCopyType IS NOT NULL)`,
                hitsPerPage: 3,
                distinct: query ? undefined : "name",
                attributesToHighlight: ["name", "authors"],
                attributesToRetrieve: [
                  "isbn",
                  "name",
                  "imageUrl",
                  "authors",
                  "language",
                ],
              },
            },
          ],
        }) as unknown as ReturnType<
          AutocompleteSource<IndexDocument>["getItems"]
        >;
      },
      templates: {
        item({ item, components }) {
          const { name, imageUrl, _highlightResult, language } =
            item as HighlightedDocument<BookItem>;
          const props: AutocompleteTemplateItemProps = {
            imageSrc: imageUrl,
            altText: `Produktbilde: ${name ?? "bok"}`,
            highlightName: components.Highlight({
              hit: item,
              attribute: "name",
            }),
            highlightDetails: _highlightResult.authors
              ? components.Highlight({
                  hit: {
                    authors:
                      _highlightResult.authors
                        .map(({ value }) => value)
                        .slice(0, 2)
                        .join(", ") +
                      (_highlightResult.authors.length > 2 ? "…" : ""),
                  },
                  attribute: "authors",
                })
              : undefined,
            itemType: "book",
            language,
          };
          return <AutocompleteTemplateItem {...props} />;
        },
      },
    };
    return [bookSource];
  };
